import { z } from 'zod';

/**
 * Invitations let an identity that has never had a password set one (docs/phases/PHASE_3.md).
 * The token is an opaque 256-bit value in base64url; only its hash is stored. Tokens travel in
 * request bodies and in the URL fragment of the link, never in a path or query string.
 */
export const invitationTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'invalid token');

/** Shown once to the platform admin who issued it (there is no email until Phase 11). */
export const issuedInvitationSchema = z.object({
  token: invitationTokenSchema,
  expiresAt: z.iso.datetime(),
});
export type IssuedInvitation = z.infer<typeof issuedInvitationSchema>;

export const invitationInspectRequestSchema = z.object({ token: invitationTokenSchema });

export const invitationInspectResponseSchema = z.object({
  email: z.string(),
  displayName: z.string(),
  expiresAt: z.iso.datetime(),
});
export type InvitationInspectResponse = z.infer<typeof invitationInspectResponseSchema>;

export const acceptInvitationRequestSchema = z.object({
  token: invitationTokenSchema,
  password: z.string().min(10).max(256),
});

/**
 * "Forgot password" (adr/0035): the sign-in page asks for a link by email. The answer is the same
 * whether or not the address has an account. The link works like an invitation link (same token
 * shape, inspected and spent with the same bodies), but it replaces an existing password.
 */
export const passwordResetRequestSchema = z.object({
  email: z.string().trim().toLowerCase().min(1).max(254),
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

/**
 * Platform staff set a new password for a store owner (adr/0035, 7): the owner's sessions end. Never
 * for a member of the platform staff.
 */
export const setOwnerPasswordRequestSchema = z.object({ password: z.string().min(10).max(256) });
export type SetOwnerPasswordRequest = z.infer<typeof setOwnerPasswordRequestSchema>;
